import type * as React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type { ContentThemeMode } from '@gitbook/app-database';
import { Icon } from '@gitbook/spine-icons';
import {
    Description,
    FieldGroup,
    Group,
    ImagePreview,
    ImagePreviewIcon,
    ImagePreviewRemoveButton,
    Label,
    ProgressiveImage,
} from '@gitbook/spine-react';
import { isUrl } from '@gitbook/util-url';

import { useSiteContext, useUpgradeSite } from '../../../state';
import { getImageResizedURL, useUploadFile } from '../../../utils';
import { PaidFeatureIndicator } from '../../Site';
import type { SitesCustomizationPanelProps } from '../types';
import { setThemedURL } from '../utils';
import { getHeaderPreviewBackground, getHeaderPreviewForeground } from './utils';

// @site-logo - Keep it synced with backend by grepping that tag.

/**
 * Settings to configure the logo for both light and dark mode.
 */
export function LogoSettings(props: SitesCustomizationPanelProps): React.ReactElement {
    const { settings } = props;
    const { site } = useSiteContext();
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesAdvancedCustomization);

    return (
        <FieldGroup>
            <Group>
                <Group>
                    <Label>Logo</Label>
                    <PaidFeatureIndicator
                        upgrade={upgrade}
                        isFeatureActive={!!settings.header.logo}
                    />
                </Group>
                <Description className="flex flex-col gap-1">
                    <p>Replace the title & icon in the site header with your logo.</p>
                    <p className="text-ui-small">Recommended width: 600px or wider.</p>
                </Description>
            </Group>
            <div className="flex gap-4">
                {Object.values(api.CustomizationThemeMode).map((mode) => (
                    <LogoPreview key={mode} {...props} themeMode={mode} upgrade={upgrade} />
                ))}
            </div>
        </FieldGroup>
    );
}

/**
 * Handles the preview and management of the logo for a given theme mode.
 */
function LogoPreview(
    props: SitesCustomizationPanelProps & { themeMode: ContentThemeMode }
): React.ReactElement {
    const { settings, setSettings, organizationId, themeMode, site } = props;

    const headerBackground = getHeaderPreviewBackground(settings, themeMode);
    const headerForeground = getHeaderPreviewForeground(settings, themeMode);

    const headerLogoURL = settings.header.logo?.[themeMode];
    const headerLogoResizedURI = headerLogoURL
        ? getImageResizedURL(headerLogoURL, {
              width: 200,
              height: 100,
              fit: 'contain',
          })
        : undefined;

    const promptAndUpload = useUploadFile();

    /**
     * Update the logo URL for the given theme mode.
     */
    const setLogoURL = (url: string | null) => {
        setSettings((prev) => ({
            ...prev,
            header: {
                ...prev.header,
                logo: setThemedURL(prev.header?.logo, themeMode, url),
            },
        }));
    };

    const removeLogo = () => {
        setLogoURL(null);
    };

    const onPressUpload = async () => {
        const metadata = await promptAndUpload(
            {
                kind: 'site_logo',
                organization: organizationId,
                site: site.id,
                accept: 'images',
            },
            { loading: 'Uploading logo…', success: 'Logo uploaded' }
        );

        if (metadata && isUrl(metadata.downloadURL)) {
            setLogoURL(metadata.downloadURL);

            analytics.logEvent(
                analytics.createSiteEvent(
                    { id: site.id, organization: organizationId },
                    {
                        type: 'site_edit_publish_upload_full_logo',
                    }
                )
            );
        }
    };

    return (
        <div className="flex flex-1 flex-col">
            <ImagePreview
                onPress={onPressUpload}
                style={{
                    aspectRatio: '2 / 1',
                    background: headerBackground,
                    color: headerForeground,
                }}
                className="py-4"
            >
                {headerLogoResizedURI ? (
                    <>
                        <ProgressiveImage
                            src={headerLogoResizedURI}
                            className="max-h-full w-full object-contain"
                            rounded
                        />
                        <ImagePreviewRemoveButton onPress={removeLogo} />
                    </>
                ) : (
                    <ImagePreviewIcon />
                )}
            </ImagePreview>
            <div className="mt-2 flex items-center gap-2 text-muted">
                {themeMode === api.CustomizationThemeMode.Light ? (
                    <>
                        <Icon.ThemeLight size={16} className="shrink-0" />
                        <div className="text-ui-small">Light mode</div>
                    </>
                ) : (
                    <>
                        <Icon.ThemeDark size={16} className="shrink-0" />
                        <div className="text-ui-small">Dark mode</div>
                    </>
                )}
            </div>
        </div>
    );
}
